import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SenseiIdDirective } from '@examdojo/browser/sensei-id';
import { AvatarColor, AvatarInitial } from '@examdojo/core/avatar';
import { HapticDirective } from '@examdojo/core/haptics';
import { IconComponent } from '@examdojo/core/icon';
import { OperatingSystem, PlatformService } from '@examdojo/platform';
import { ProfileAvatarComponent } from '@examdojo/user';
import { TranslocoPipe } from '@jsverse/transloco';
import { injectNavSegment } from '../sidenav/nav-segment';
import { isNavItemActive, SidenavItem } from '../sidenav/sidenav.model';

export type TabBarProfileAppearance = 'default' | 'rail';

/**
 * The compact surface of the shell nav, natively and in a browser alike: a
 * floating pill over the page rather than a bar across its foot, so the content
 * keeps scrolling behind it (Figma 3212:259873).
 *
 * Both platforms are drawn, from one item list: iOS stacks a 26px icon over the
 * label inside the selected pill, Android puts a 24px icon in a pill of its own
 * and hangs the label below it. Everything else — the geometry, the colours, the
 * greying of an unselected illustration — is shared.
 *
 * The selection is one element that slides, not a background that moves from
 * tab to tab: the tabs share the pill's width equally, so the indicator is
 * `100% / count` wide and travels by whole multiples of itself. That is a
 * transform and nothing else, which is what keeps the move on the compositor.
 */
@Component({
  selector: 'dojo-tab-bar',
  standalone: true,
  imports: [RouterLink, TranslocoPipe, IconComponent, HapticDirective, SenseiIdDirective, ProfileAvatarComponent],
  templateUrl: './tab-bar.component.html',
  styleUrl: './tab-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Over the page and clear of the pointer: only the pill takes a touch, so the
  // content either side of it on a tablet stays scrollable. `bottom` is the
  // stylesheet's, which keeps the bar at rest while the shell follows the
  // keyboard.
  host: {
    class: 'pointer-events-none absolute inset-x-0 z-2',
    '[class.tab-bar--android]': 'isAndroid',
  },
})
export class TabBarComponent {
  private readonly platformService = inject(PlatformService);

  readonly items = input.required<SidenavItem[]>();
  /** The tab drawn as the signed-in user's avatar rather than an illustration. */
  readonly profileRoute = input<string | null>(null);
  /**
   * `rail` draws the avatar as a person on the rail is drawn: a bold initial, grey until
   * its tab is selected. A picture is shown as it is either way.
   */
  readonly profileAppearance = input<TabBarProfileAppearance>('default');
  /** The avatar's colour wherever it is not greyed. */
  readonly profileColor = input<AvatarColor>('name');

  readonly isAndroid = this.platformService.operatingSystem === OperatingSystem.Android;

  readonly iconSize = this.isAndroid ? 24 : 26;

  private readonly segment = injectNavSegment();

  /**
   * Which tab the indicator stands over, or `-1` where the page under the bar
   * belongs to none of them and it is not drawn.
   *
   * The URL segment rather than `routerLinkActive`: that one publishes a class
   * onto the anchor, and the indicator is a sibling of all five. The same index
   * then colours the tab, so the pill and the label can never disagree.
   */
  readonly activeIndex = computed(() => this.items().findIndex((item) => isNavItemActive(item, this.segment())));

  readonly profileAvatarColor = computed<AvatarColor>(() => {
    const profileSelected = this.items()[this.activeIndex()]?.route === this.profileRoute();
    return this.profileAppearance() === 'rail' && !profileSelected ? 'neutral' : this.profileColor();
  });

  readonly profileAvatarInitial = computed<AvatarInitial>(() =>
    this.profileAppearance() === 'rail' ? 'display' : 'fitted',
  );

  labelKey(item: SidenavItem): string {
    return this.platformService.isPhoneLayout() ? (item.labelKeyPhone ?? item.labelKey) : item.labelKey;
  }
}
